import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { ObjectGroupViewModel } from "../system/admin-panel/ObjectGroupViewModel";
import { IManagableObject } from "../system/admin-panel/IManagableObject";
import { Observable, throwError } from "rxjs";
import { IOwnerManipulationService, ShareObjectViewModel } from "./IOwnerManipulationService";
import { KeySyncViewModel } from "../system/admin-panel/Keys/Model/KeyViewModel";
import { catchError, finalize, map, tap } from "rxjs/operators";
import { SyncService } from './sync.service';
import { ObjectModel } from "../models/ObjectModel";
import { IconViewModel } from "../models/IconViewModel";
import { TrendIconViewModel } from "../models/TrendIconViewModel";
import { DeviceModelViewModel } from "../models/DeviceModelViewModel";
import { ObjectLiteViewModel } from "../models/ObjectLiteViewModel";
import {OdometerViewModel} from "../models/OdometerViewModel";
import {ObjectImportResult} from "../models/ObjectImportResult";
import {LocalizationService} from "../system/mobile-system/services/localization.service";
import {UsersService} from "./users.service";


@Injectable()
export class ObjectsService implements IOwnerManipulationService {

  constructor(private client: HttpClient,
              private syncService: SyncService,
              private usersService: UsersService,
              private localizationService: LocalizationService) {

  }

  public GetObjects() {
    return this.client.get<any>("/ctrl/objects").pipe(map(values => {
      let result = [];
      if (values && values.constructor === Array) {
        for (let item of values) {
          let obj = ObjectModel.fromJS(item);
          obj.CreateDate = this.localizationService.toUserTimeZoneDate(obj.CreateDate, this.usersService.getCurrentUser());
          result.push(obj);
        }
      }

      return result;
    }))
  }

  public GetObject(id: number) {
    return this.client.get<any>("/ctrl/objects/" + id).pipe(map(data => {
      return ObjectModel.fromJS(data);
    }));
  }

  public GetLiteObjects(): Observable<ObjectLiteViewModel[]>  {
    return this.client.get<any>("/ctrl/objects/lite").pipe(map(values => {
      let result = [];
      if (values && values.constructor === Array) {
        for (let item of values)
          result.push(ObjectLiteViewModel.fromJS(item))
      }

      return result;
    }))
  }

  public GetOdometer(id: number): Observable<OdometerViewModel> {
    return this.client.get<OdometerViewModel>("/ctrl/objects/odometer/" + id).pipe(map(data => {
      return OdometerViewModel.fromJS(data);
    }));
  }

  public GetObservedObjects() {
    return this.client.get<any>("/ctrl/objects/observed").pipe(map(values => {
      let result = [];
      if (values && values.constructor === Array) {
        for (let item of values) {
          let obj = ObjectModel.fromJS(item);
          obj.CreateDate = this.localizationService.toUserTimeZoneDate(obj.CreateDate, this.usersService.getCurrentUser());
          result.push(obj);
        }
      }
      return result;
    }))
  }

  public GetUserObjects(userId: number): Observable<ObjectLiteViewModel[]>  {
    return this.client.get<any>("ctrl/ObjectShare/user/" + userId).pipe(map(values => {
      let result = [];
      if (values && values.constructor === Array) {
        for (let item of values) {
          let obj = ObjectLiteViewModel.fromJS(item);
          obj.CreateDate = this.localizationService.toUserTimeZoneDate(obj.CreateDate, this.usersService.getCurrentUser());
          result.push(obj);
        }
      }

      return result;
    }))
  }

  public GetByCompanyId(companyId: number): Observable<ObjectLiteViewModel[]> {
    return this.client.get<any>("ctrl/ObjectShare/company/" + companyId).pipe(map(values => {
      let result = [];
      if (values && values.constructor === Array) {
        for (let item of values) {
          let obj = ObjectLiteViewModel.fromJS(item);
          obj.CreateDate = this.localizationService.toUserTimeZoneDate(obj.CreateDate, this.usersService.getCurrentUser());
          result.push(obj);
        }
      }

      return result;
    }))
  }
  ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////
  public GetSyncKeysForObject(targetId: number) {
    return this.client.get<any>("/ctrl/objects/sync/" + targetId).pipe(map(values => {
      let result = [];
      if (values && values.constructor === Array) {
        for (let item of values)
          result.push(KeySyncViewModel.fromJS(item, true))
      }
      return result;
    }))
  }

  public SetSyncKeysForObject(targetId: number, keyIds: number[]) {
    return this.client.put<any>("/ctrl/objects/sync/" + targetId, keyIds);
  }

  ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////
  public GetObjectGroups() {
    return this.client.get<any>("/ctrl/objects/groups").pipe(map(values => {
      let result = [];
      if (values && values.constructor === Array) {
        for (let item of values)
          result.push(ObjectGroupViewModel.fromJS(item))
      }

      return result;
    }));
  }


  public StopSharing(fromUserId: number, toUserId: number, objectIds: number[]) {
    let input = new ShareObjectViewModel(fromUserId, toUserId, objectIds);
    return this.client.post("ctrl/ObjectShare/StopSharing", input).pipe(map(res => {
      this.syncService.NotifyEntityChanged("Profile", 0);
      return res;
    }));
  }

  public SetOwner(fromUserId: number, toUserId: number, ids: number[]) {
    let input = new ShareObjectViewModel(fromUserId, toUserId, ids);
    return this.client.post("ctrl/ObjectShare/SetOwner", input).pipe(map(res => {
      this.syncService.NotifyEntityChanged("Profile", 0);
      return res;
    }));
  }

  public ShareObject(fromUserId: number, toUserId: number, ids: number[]) {
    let input = new ShareObjectViewModel(fromUserId, toUserId, ids);
    return this.client.post("ctrl/ObjectShare/Share", input).pipe(map(res => {
      this.syncService.NotifyEntityChanged("Profile", 0);
      return res;
    }));
  }

  public GetIcons() {
    return this.client.get<any>("/ctrl/objects/icons").pipe(map(values => {
      let result = [];
      if (values && values.constructor === Array) {
        for (let item of values)
          result.push(IconViewModel.fromJS(item))
      }

      return result;
    }));
  }

  public GetTrendIcons() {
    return this.client.get<any>("/ctrl/objects/trendIcons").pipe(map(values => {
      let result: TrendIconViewModel[] = [];
      if (values && values.constructor === Array) {
        for (let item of values)
          result.push(TrendIconViewModel.fromJS(item))
      }

      return result;
    }));
  }

  public CreateObject(model: ObjectModel) {
    return this.client.post("/ctrl/objects", model).pipe(map(() => {
      this.syncService.NotifyEntityChanged("Object", model.Id);
    }));
  }

  public EditObject(model: ObjectModel) {
    return this.client.put("/ctrl/objects", model).pipe(map(() => {
      this.syncService.NotifyEntityChanged("Object", model.Id);
    }));
  }
  /*
  public EditObjectAsOwner(model: ObjectViewModel) {
    return this.client.put("/ctrl/objects/asown/", model).pipe(map(() => {
      this.syncService.NotifyEntityChanged("Object", model.Main.Id);
    }));
  }
   */

  public CreateGroup(input: IManagableObject): Observable<any> {
    if (input.Id > 0)
      return this.client.put("/ctrl/objects/groups", input);
    else
      return this.client.post("/ctrl/objects/groups", input);
  }

  public DeleteGroup(input: IManagableObject): Observable<any> {
    return this.client.delete("/ctrl/objects/groups/" + input.Id);
  }

  public DeleteObject(id: number): Observable<any> {
    return this.client.delete("/ctrl/objects/" + id).pipe(map(data => {
      return ObjectModel.fromJS(data);
    }));
  }

  public DeleteObjects(ids: number[]): Observable<any> {
    return this.client.delete("/ctrl/objects/delete", { body: ids });
  }

  public DeleteObjectAsOwner(id: number): Observable<any> {
    return this.client.delete("/ctrl/objects/asown/" + id).pipe(map(data => {
      this.syncService.NotifyEntityChanged("Object", id);
      return ObjectModel.fromJS(data);
    }));
  }

  public GetDetailsAsOwner(id: number) {
    return this.client.get<any>("/ctrl/objects/asown/" + id).pipe(map(data => {
      return ObjectModel.fromJS(data);
    }));
  }
  public SetPayedTill(ids: number[], till: number) {
    return this.client.post<any>("/ctrl/objects/payedtill", { "objectIds": ids, "time": till });
  }

  public GetObjectsModels() {
    return this.client.get<any>("/ctrl/objects/models/")
      .pipe(map(values => {
        let result = [];
        if (values && values.constructor === Array) {
          for (let item of values)
            result.push(DeviceModelViewModel.fromJS(item))
        }
        return result;
      }));
  }

  public SetObjectsIcon(objects: ObjectModel[], icon: IconViewModel) {
    let ids: number[] = [];
    for (let i = 0; i < objects.length; i++)
      ids.push(objects[i].Id);

    return this.client.post("/ctrl/objects/seticon/" + icon.Id, ids).pipe(map(() => {
      this.syncService.NotifyEntityChanged("Object", 0);
    }));
  }

  importFromFile(model): Observable<ObjectImportResult> {
    const formData = new FormData();
    formData.append('Content', model.Content);
    formData.append('OverwriteExisting', model.OverwriteExisting);
    return this.client.post<any>('ctrl/ObjectImport/import', formData)
      .pipe(map(data => {
        return ObjectImportResult.fromJS(data);
      }));

  }

  exportJson(ids: number[]): Observable<Blob> {
    return this.client.post('ctrl/ObjectImport/export-json', ids, {
      responseType: 'blob'
    });
  }
}
